import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Typography } from '@mui/material';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import CreateTable from 'src/components/ui-component/form-component/create-form/CreateTable';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import { useAuthentication } from 'src/context/AuthContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import { withKostenkategorieEinsatzbereichFilter } from 'src/utils/einsatzbereichHelper';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	AUSGABE_KATEGORIE,
	ERLAUTERUNG_IN_KOSTEN_TITLE,
	FORM_FIELDS_TYPES,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	KOSTENTRAEGER,
	PARTNER_DATEN_NICHT_ERFASST,
	PLATZHALTER_TEXT,
} from 'src/lib/constants/constant';
import { FormStrucItem, Validation } from 'src/lib/types/types';
import { ForceTrue, GetMaxDecimalWithStaticMinValidation, Required } from 'src/lib/validation/ValidationTypes';
import { checkIfSpecificKostenPositionHasComment } from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import { getComponentTableByFormStruc } from 'src/utils/formStruc';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, enumT, getOptionsFromRes, isTrue, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import PruefAssistenz from '../../../../antrag-components/PruefAssistenz';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';
import { getBasisDataKostenKategorie, getKostenErlauterung } from '../../../../application-helper-function/AppHelperfunctions';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = AntragSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const InvestitionskostenAntragEbusse: FC<{ hasComment: boolean }> = ({ hasComment }) => {
	const { authenticatedData } = useAuthentication();
	const unternehmenId = get(authenticatedData, 'organisationId');
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const [allKostenKategories, setAllKostenKategories] = useState([]);

	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleAddInvestitionsKosten = async () => {
		// autofold/fold_next
		let appCopy = cloneDeep(application);
		const data = getBasisDataKostenKategorie(
			appCopy,
			allKostenKategories, // All kostenkategorie for Sachkosten, to find default Kostenkategorie id
			KOSTENTRAEGER.Antragsteller,
			unternehmenId,
			KOSTEN_POSITION.Ausgabe,
			KOSTEN_ARTEN.Investitionskosten,
		);
		set(
			appCopy,
			addIndexesToPath(
				P.Foerderantrag_Kostenpositions.path,
				[`${get(appCopy, P.Foerderantrag_Kostenpositions.path, []).length}`],
				['Foerderantrag_Kostenpositions'],
			),
			{ attributes: data },
		);
		saveApplication(false, appCopy);
	};
	const handleDeleteInvestitionKosten = async (id: number) => {
		// autofold/fold_next
		try {
			let appcopy = cloneDeep(application);
			const allKostenposition = get(appcopy, P.Foerderantrag_Kostenpositions.path, []);
			set(
				appcopy,
				P.Foerderantrag_Kostenpositions.path,
				allKostenposition.filter((item: any) => item.id !== id),
			);
			let res = await DeleteRecord(APIS.Kostenposition, id);
			if (res) {
				saveApplication(false, appcopy);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Personalkosten', err);
		}
	};

	const RenderkalkulationBlock = (index: number, allKostenKategories: any) => {
		// autofold/fold_next
		const selectedKategorie = getSelectedKostenkatorie(index, application, allKostenKategories);

		switch (selectedKategorie) {
			case AUSGABE_KATEGORIE.Elektrofahrzeug:
				return (
					<>
						<Typography variant='h5'>Kalkulation Eletrobusse</Typography>
						<CreateTable
							formStruc={mapKosten(formStrucKalkulationElektroFahrzeug(), index)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
							columnsLabel={['', 'Hersteller / Bezeichnung', 'Grundpreis (€)', 'Spezifische Ausstattung (€)', 'Fahrzeugpreis (€)']}
						/>
						<CreateForm
							formStruc={mapKosten(formStrucKalkulationElektroFahrzeugGesamt(), index)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
							api={APIS.Kostenposition}
						/>
						<CreateForm
							formStruc={mapKosten(formStrucKalkulationElektroFahrzeugKostenProJahr(application, index), index)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
							api={APIS.Kostenposition}
						/>
					</>
				);
			case AUSGABE_KATEGORIE.LadeTankInfrastruktur:
				return (
					<>
						<CreateForm
							formStruc={mapKosten(formStrucKalkulateionLadeInfrastruktur(), index)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
							api={APIS.Kostenposition}
						/>
						<CreateForm
							formStruc={mapKosten(formStrucKostenProJahr(application, index), index)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
							api={APIS.Kostenposition}
						/>
					</>
				);
			case AUSGABE_KATEGORIE.NetzanschlussUndErdarbeiten:
				return (
					<>
						<CreateForm
							formStruc={mapKosten(formStrucKalkulateionNetzanschluss(), index)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
							api={APIS.Kostenposition}
						/>
						<CreateForm
							formStruc={mapKosten(formStrucKostenProJahr(application, index), index)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
							api={APIS.Kostenposition}
						/>
					</>
				);
			case AUSGABE_KATEGORIE.H2:
				return (
					<>
						<CreateForm
							formStruc={mapKosten(formStrucKostenProJahr(application, index), index)}
							formValues={application}
							updateFormValuesFunc={setApplicationProperty}
							readOnly={updatedReadOnly}
							api={APIS.Kostenposition}
						/>
					</>
				);
			default:
				return <></>;
		}
	};

	// Fetching kostenKategories
	useEffect(() => {
		// autofold/fold_next
		const fetch = async () => {
			try {
				const filter = withKostenkategorieEinsatzbereichFilter({
					Allgemein_Kostenposition: KOSTEN_POSITION.Ausgabe,
					Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Investitionskosten,
					Allgemein_Antragsformular: { $contains: get(application, P.Allgemein_Antragsformular.path) },
					Allgemein_Aktiv: { $eq: true },
				}, APIS.Antrag);
				let res = await GetRecords(APIS.Kostenkategorie, [], filter);
				setAllKostenKategories(removeNullDeep(removeDataKeys(get(res, 'data', []))));
			} catch (err: any) {
				console.error('ERROR: Fetching KostenKategories ', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title='Investitionskosten' description={PLATZHALTER_TEXT} />
			{/* eslint-disable-next-line array-callback-return */}
			{get(application, P.Foerderantrag_Kostenpositions.path, []).map((eachKosten: any, i: number) => {
				if (
					get(eachKosten, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
					get(eachKosten, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Investitionskosten &&
					get(eachKosten, `${KP_P.Unternehmen.path}.id`) === unternehmenId
				) {
					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle={`${enumT(KOSTEN_ARTEN.Investitionskosten)}`}
							onDelete={() => handleDeleteInvestitionKosten(get(eachKosten, 'id'))}
							showDeleteButton={!updatedReadOnly}
							hasComment={checkIfSpecificKostenPositionHasComment(readOnly, eachKosten)}
						>
							<PruefAssistenz
								statusPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Pruefstatus.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								hinweisPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Hinweis_Fuer_Antragsteller.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								ueberarbeitetAmPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Ueberarbeitet_Am.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								erledigtPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								formStruc={mapKosten(PruefAssistenzStruc(), i)}
							/>

							<MUIAlert
								title={ERLAUTERUNG_IN_KOSTEN_TITLE}
								message={getKostenErlauterung(allKostenKategories, get(eachKosten, KP_P.Allgemein_Kostenkategorie.path))}
							/>

							<CreateForm
								formStruc={mapKosten(formStrucAllgemein(allKostenKategories), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
								api={APIS.Kostenposition}
							/>

							<Box sx={{ mt: '1rem' }}>{RenderkalkulationBlock(i, allKostenKategories)}</Box>
						</MUIAccordionComponent>
					);
				}
			})}
			{unternehmenId ?
				<Button
					sx={{ mt: '2rem', maxWidth: 'fit-content' }}
					endIcon={<AddIcon />}
					onClick={handleAddInvestitionsKosten}
					disabled={updatedReadOnly}
				>
					Investitionskosten hinzufügen
				</Button>
			:	<MUIAlert message={PARTNER_DATEN_NICHT_ERFASST} />}
		</>
	);
};

export default InvestitionskostenAntragEbusse;

const PruefAssistenzStruc = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};
const formStrucAllgemein = (allKostenKategories?: any[]) => {
	// autofold/fold_next
	const kategorieOptions = allKostenKategories || [];
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie,
					type: 'enumeration',
					options: getOptionsFromRes(kategorieOptions, 'Allgemein_Bezeichnung'),
				},
				P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Bezeichnung,
			],
		},
	];
	return struc;
};
// Für ElektroBusse
const formStrucKalkulationElektroFahrzeug = () => {
	// autofold/fold_next
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ path: 't.t', type: 'label', label: 'Verbrennerbus (Dieselbus)' },
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
					.Kalkulation_Der_Investit_0006792225_sse_Fahrzeug_Block_1_Zelle_1,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
						.Kalkulation_Der_Investit_0006792226_sse_Fahrzeug_Block_1_Zelle_2,
					format: 'currency',
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
						.Kalkulation_Der_Investit_0006792227_sse_Fahrzeug_Block_1_Zelle_3,
					format: 'currency',
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
						.Kalkulation_Der_Investit_0006792228_sse_Fahrzeug_Block_1_Zelle_4,
					format: 'currency',
					disabled: true,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [
				{ path: 't.t', type: 'label', label: 'Elektrobus' },
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
					.Kalkulation_Der_Investit_0006792241_sse_Fahrzeug_Block_2_Zelle_1,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
						.Kalkulation_Der_Investit_0006792242_sse_Fahrzeug_Block_2_Zelle_2,
					format: 'currency',
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
						.Kalkulation_Der_Investit_0006792243_sse_Fahrzeug_Block_2_Zelle_3,
					format: 'currency',
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
						.Kalkulation_Der_Investit_0006792244_sse_Fahrzeug_Block_2_Zelle_4,
					format: 'currency',
					disabled: true,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucKalkulationElektroFahrzeugGesamt = (selectedProjektbezogeneAusgabenPath?: string) => {
	// autofold/fold_next
	let validation: Validation[] = [];
	if (selectedProjektbezogeneAusgabenPath) {
		validation = [
			{ validateFunction: Required, disabledValidation: false },
			{
				validateFunction: GetMaxDecimalWithStaticMinValidation,
				params: [
					selectedProjektbezogeneAusgabenPath,
					'',
					'Der Vergleichswert darf nicht negativ sein (siehe Investitionsmehrausgaben).',
					'Investitionsmehrausgaben muss für einen Vergleich gesetzt werden.',
				],
				disabledValidation: false,
			},
		];
	}
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
						.Kalkulation_Der_Investit_0185967294_sse_Investitionsmehrausgaben,
					format: 'currency',
					disabled: true,
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
						.Zelle_2,
					format: 'currency',
					disabled: true,
					label: 'Geplante Gesamtsumme',
					validation,
				},
			],
		},
	];
	return struc;
};
const formStrucKalkulationElektroFahrzeugKostenProJahr = (application: any, i: number) => {
	// autofold/fold_next
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
						.Projekt_Jahr,
					type: 'componentTable',
					maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
					disabledColumns: ['Zelle_1', 'Zelle_3'],
					fieldProps: { Zelle_2: { format: 'currency' } },
					hideDeleteButton: true,
					sumRow: [
						{
							id: 'pinnedRow',
							Zelle_1: 'Gesamt',
							Zelle_2: get(
								application,
								addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema
										.attributes.Zelle_2.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								),
							),
							Zelle_3: get(
								application,
								addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema
										.attributes.Zelle_3.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								),
							),
						},
					],
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];
	return struc;
};
// Für LadeInfrastruktur
const formStrucKalkulateionLadeInfrastruktur = (selectedProjektbezogeneAusgabenPath?: string) => {
	// autofold/fold_next
	let validation: Validation[] = [];
	if (selectedProjektbezogeneAusgabenPath) {
		validation = [
			{ validateFunction: Required, disabledValidation: false },
			{
				validateFunction: GetMaxDecimalWithStaticMinValidation,
				params: [
					selectedProjektbezogeneAusgabenPath,
					'',
					'Der Vergleichswert darf nicht negativ sein (siehe Projektbezogene Ausgaben).',
					'Projektbezogene Ausgaben muss für einen Vergleich gesetzt werden.',
				],
				disabledValidation: false,
			},
		];
	}
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Infrastruktur_Ladesaeulen_Elektrobusse.schema.attributes
					.Kalkulation_Der_Ausgaben_0085085636_ler_Bezeichnung_Ggf_Standort,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Infrastruktur_Ladesaeulen_Elektrobusse.schema.attributes
						.Kalkulation_Der_Ausgaben_0183571662_robusse_Kalkulierte_Ausgaben,
					format: 'currency',
				},
			],
		},
		{
			fields: [
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Infrastruktur_Ladesaeulen_Elektrobusse.schema.attributes
					.Kalkulation_Der_Ausgaben_0212753751_KW_Installierte_Ladeleistung,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Infrastruktur_Ladesaeulen_Elektrobusse.schema.attributes
						.Kalkulation_Der_Ausgaben_0070636583_Ausgaben_Pro_KW_Ladeleistung,
					format: 'currency',
					disabled: true,
				},
			],
		},
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Infrastruktur_Ladesaeulen_Elektrobusse.schema.attributes
						.Kalkulation_Der_Ausgaben_0119491950_usse_Projekbezogene_Ausgaben,
					format: 'currency',
					disabled: true,
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme,
					format: 'currency',
					disabled: true,
					validation,
				},
			],
		},
	];
	return struc;
};
// Für Netzanschluss
const formStrucKalkulateionNetzanschluss = (selectedProjektbezogeneAusgabenPath?: string) => {
	// autofold/fold_next
	let validation: Validation[] = [];
	if (selectedProjektbezogeneAusgabenPath) {
		validation = [
			{ validateFunction: Required, disabledValidation: false },
			{
				validateFunction: GetMaxDecimalWithStaticMinValidation,
				params: [
					selectedProjektbezogeneAusgabenPath,
					'',
					'Der Vergleichswert darf nicht negativ sein (siehe Projektbezogene Ausgaben).',
					'Projektbezogene Ausgaben muss für einen Vergleich gesetzt werden.',
				],
				disabledValidation: false,
			},
		];
	}
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Netzanschluss_Elektrobusse.schema.attributes
					.Kalkulation_Der_Ausgaben_0146711575_se_Standort_Und_Beschreibung,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Netzanschluss_Elektrobusse.schema.attributes
						.Kalkulation_Der_Ausgaben_0069253156_obusse_Ausgaben_Pro_Standort,
					format: 'currency',
					disabled: true,
				},
			],
		},
		{
			fields: [
				P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Netzanschluss_Elektrobusse.schema.attributes
					.Kalkulation_Der_Ausgaben_0195029461_lektrobusse_Anzahl_Standorte,
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Netzanschluss_Elektrobusse.schema.attributes
						.Kalkulation_Der_Ausgaben_0054013598_usse_Projekbezogene_Ausgaben,
					format: 'currency',
					disabled: true,
				},
			],
		},
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Netzanschluss_Elektrobusse.schema.attributes
						.Kalkulation_Der_Ausgaben_0055858862_robusse_Kalkulierte_Ausgaben,
					format: 'currency',
				},
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme,
					format: 'currency',
					disabled: true,
					validation,
				},
			],
		},
	];
	return struc;
};
// Für H2 Tankstelle
const formStrucKalkulationH2 = () => {
	// autofold/fold_next
	let struc: FormStrucItem[] = [
		{
			fields: [P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme],
		},
	];
	return struc;
};
// Allgemein Kosten pro Jahr Tabelle
// AUßer Elektrobusse
const formStrucKostenProJahr = (application: any, i: number) => {
	// autofold/fold_next
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kosten_Pro_Jahr,
					type: FORM_FIELDS_TYPES.editableTable,
					maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
					disabledColumns: ['Zelle_1'],
					fieldProps: { Zelle_2: { format: 'currency' } },
					hideDeleteButton: true,
					sumRow: [
						{
							id: 'pinnedRow',
							Zelle_1: 'Gesamt',
							Zelle_2: get(
								application,
								addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								),
							),
						},
					],
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];
	return struc;
};

const mapKosten = (formStruc: FormStrucItem[], index: number) => {
	// autofold/fold_next
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Foerderantrag_Kostenpositions.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Foerderantrag_Kostenpositions.path.length),
				};
			}),
		};
	});
	return result;
};
const getSelectedKostenkatorie = (index: number, application: any, kostenKategorie: any[]) => {
	let selectecKatogorie = get(
		application,
		addIndexesToPath(
			P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kostenkategorie.path,
			[`${index}`],
			['Foerderantrag_Kostenpositions'],
		),
	);

	if (typeof selectecKatogorie === 'object') {
		return get(selectecKatogorie, 'attributes.Allgemein_Ausgabekategorie');
	} else {
		let kategorieObj = kostenKategorie.find((ele) => parseInt(get(ele, 'id')) === parseInt(selectecKatogorie));
		return get(kategorieObj, 'attributes.Allgemein_Ausgabekategorie');
	}
};

const mapSelectedPath = (parentPath: string, childPath: string, index: number) => {
	const path = childPath.slice(0, parentPath.length) + `.${index}` + childPath.slice(parentPath.length);
	return path;
};

export const getFormStrucInvestitionskostenAntragEbusse = (application: any, unternehmenId: number, kostenKategorie: any) => {
	let result: FormStrucItem[] = [];
	const allKostenPositions = get(application, P.Foerderantrag_Kostenpositions.path, []);
	allKostenPositions.forEach((eachKosten: any, i: number) => {
		if (
			get(eachKosten, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
			get(eachKosten, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Investitionskosten &&
			get(eachKosten, `${KP_P.Unternehmen.path}.id`) === unternehmenId
		) {
			const selectedKategorie = getSelectedKostenkatorie(i, application, kostenKategorie);
			let selectedProjektbezogeneAusgabenPath = '';
			const formStrucAllgemeinWithIndex = mapKosten(formStrucAllgemein(), i);
			//Not used in Ebus
			// const formStrucKostenProJahrWithIndex =  getComponentTableByFormStruc(mapKosten(formStrucKostenProJahr(application, i), i))
			// console.log("formStrucKostenProJahrWithIndex", formStrucKostenProJahrWithIndex)

			result = concat(result, formStrucAllgemeinWithIndex);

			if (selectedKategorie === AUSGABE_KATEGORIE.Elektrofahrzeug) {
				selectedProjektbezogeneAusgabenPath = mapSelectedPath(
					P.Foerderantrag_Kostenpositions.path,
					P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Investitionsmehrausgaben_Fuer_Elektrobusse.schema.attributes
						.Kalkulation_Der_Investit_0185967294_sse_Investitionsmehrausgaben.path,
					i,
				);
				const elektroFahrzeugStrucWithIndex = mapKosten(formStrucKalkulationElektroFahrzeug(), i);
				const elektroFahrzeugStrucGesamtWithIndex = mapKosten(formStrucKalkulationElektroFahrzeugGesamt(selectedProjektbezogeneAusgabenPath), i);
				const elektroFahrzeugStrucKostenProJahrWithIndex = getComponentTableByFormStruc(
					mapKosten(formStrucKalkulationElektroFahrzeugKostenProJahr(application, i), i),
				);
				result = concat(result, elektroFahrzeugStrucWithIndex, elektroFahrzeugStrucGesamtWithIndex, elektroFahrzeugStrucKostenProJahrWithIndex);
			}
			if (selectedKategorie === AUSGABE_KATEGORIE.LadeTankInfrastruktur) {
				selectedProjektbezogeneAusgabenPath = mapSelectedPath(
					P.Foerderantrag_Kostenpositions.path,
					P.Foerderantrag_Kostenpositions.schema.attributes.Kalkulation_Der_Ausgaben_Infrastruktur_Ladesaeulen_Elektrobusse.schema.attributes
						.Kalkulation_Der_Ausgaben_0119491950_usse_Projekbezogene_Ausgaben.path,
					i,
				);
				const formStrucKalkulateionLadeInfrastrukturWithIndex = mapKosten(
					formStrucKalkulateionLadeInfrastruktur(selectedProjektbezogeneAusgabenPath),
					i,
				);

				result = concat(result, formStrucKalkulateionLadeInfrastrukturWithIndex);
			}
			if (selectedKategorie === AUSGABE_KATEGORIE.NetzanschlussUndErdarbeiten) {
				const formStrucKalkulateionNetzanschlussWithIndex = mapKosten(formStrucKalkulateionNetzanschluss(), i);
				result = concat(result, formStrucKalkulateionNetzanschlussWithIndex);
			}
			if (selectedKategorie === AUSGABE_KATEGORIE.H2) {
				const formStrucKalkulationH2WithIndex = mapKosten(formStrucKalkulationH2(), i);
				result = concat(result, formStrucKalkulationH2WithIndex);
			}
			const isHinweisFieldRequired =
				get(eachKosten, KP_P.Pruefung_Hinweis_Fuer_Antragsteller.path) && !isTrue(get(eachKosten, KP_P.Pruefung_Erledigt.path));
			if (isHinweisFieldRequired) result = concat(result, mapKosten(PruefAssistenzStruc(), i));
		}
	});

	return result;
};
